"use client";

import * as React from "react";
import { Button, toast } from "@aio/platform-ui";
import {
  DataTable,
  defineColumns,
  toCsv,
  toXlsx,
  toPdf,
  type ColumnDef,
  type ExportFormat,
  type FormatterBundle,
} from "@aio/platform-table";
import {
  useLocale,
  formatNumber,
  formatCurrency,
  formatDate,
} from "@aio/platform-i18n";
import { Plus } from "lucide-react";

/**
 * One row in the showcase invoice dataset.
 *
 * The shape exercises every column type the engine supports:
 * - text     → `number`
 * - relation → `partnerId` resolved via `relationLabel`
 * - money    → `total`
 * - percent  → `vatRate`
 * - date     → `dueDate`
 * - datetime → `issuedAt`
 * - enum     → `status`
 * - boolean  → `isPaid`
 */
interface InvoiceRow {
  id: string;
  number: string;
  partnerId: string;
  partnerName: string;
  total: number;
  vatRate: number;
  dueDate: Date;
  issuedAt: Date;
  status: "draft" | "sent" | "paid" | "overdue" | "void";
  isPaid: boolean;
}

/** Display labels for the `status` enum column. */
const STATUS_LABELS: Record<InvoiceRow["status"], string> = {
  draft: "Draft",
  sent: "Sent",
  paid: "Paid",
  overdue: "Overdue",
  void: "Void",
};

/**
 * Stable partner registry. The `relation` column resolves the partner id to
 * a display name via `relationLabel`. Real modules read from a partner
 * lookup; here a hand-built map is enough to demo the relation column.
 */
const PARTNERS: Record<string, string> = {
  "p-001": "Apoteka Centar d.o.o.",
  "p-002": "Beograd Trade",
  "p-003": "Cvetanović Inženjering",
  "p-004": "Delta Logistika",
  "p-005": "Elektromreža Srbije",
  "p-006": "Fortuna Putovanja",
  "p-007": "Galenika a.d.",
  "p-008": "Henkel Beograd",
  "p-009": "Imlek Beograd",
  "p-010": "Jugohemija",
};

/**
 * Generate the 1000-row invoice dataset that drives the virtualizer demo.
 *
 * Determinism: every value is derived from the row index `i`. Tests render
 * snapshots of this page so the dataset MUST be reproducible across runs —
 * Math.random would break visual baselines.
 *
 * Distribution choices:
 * - `total` spans 100..50_100 EUR so money formatting handles thousands.
 * - `vatRate` cycles 0%, 10%, 20% — Serbia's actual rates.
 * - Dates anchor at 2026-01-01 and step backwards two years so the date
 *   filter has range and SSR / CSR agree on the rendered output.
 * - `status` cycles through all five enum values uniformly.
 * - `isPaid` is true for `status === "paid"` and false otherwise — keeps
 *   the boolean column meaningfully correlated rather than random.
 */
function generateInvoices(count: number): InvoiceRow[] {
  const statuses: InvoiceRow["status"][] = [
    "draft",
    "sent",
    "paid",
    "overdue",
    "void",
  ];
  const partnerIds = Object.keys(PARTNERS);
  // Anchor the date generator to a fixed point so SSR and CSR produce the
  // same output. Using `new Date()` would drift between renders.
  const ANCHOR = new Date("2026-01-01T00:00:00Z").getTime();
  const DAY_MS = 24 * 60 * 60 * 1000;

  return Array.from({ length: count }, (_, i): InvoiceRow => {
    const status = statuses[i % statuses.length] as InvoiceRow["status"];
    const partnerId = partnerIds[i % partnerIds.length] as string;
    const issuedOffsetDays = i % 730;
    const issuedAt = new Date(ANCHOR - issuedOffsetDays * DAY_MS);
    const dueDate = new Date(issuedAt.getTime() + 30 * DAY_MS);
    return {
      id: `inv-${String(i + 1).padStart(4, "0")}`,
      number: `INV-2026-${String(i + 1).padStart(4, "0")}`,
      partnerId,
      partnerName: PARTNERS[partnerId] ?? "Unknown",
      total: 100 + ((i * 137) % 50_000),
      vatRate: [0, 0.1, 0.2][i % 3] as number,
      dueDate,
      issuedAt,
      status,
      isPaid: status === "paid",
    };
  });
}

/** Single static dataset for the page — see generator's determinism note. */
const ROWS = generateInvoices(1000);

/**
 * Trigger a browser download for an export Blob. The dance with
 * `URL.createObjectURL` + a transient anchor element is the standard way to
 * push a generated Blob to disk in browsers without a server round-trip.
 *
 * Cleanup: `revokeObjectURL` after the click frees the blob memory. Without
 * it, every export leaks the underlying ArrayBuffer until the tab unloads.
 */
function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Release the blob URL on the next tick so the click handler has time to
  // dispatch the download before the URL becomes invalid.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export default function TableShowcase(): React.JSX.Element {
  const { locale } = useLocale();

  /**
   * Wire `@aio/platform-i18n` formatters into the table. The bundle uses
   * plain `string` for locale (the table package doesn't import the i18n
   * provider) so we adapt at the call site.
   */
  const formatters = React.useMemo<FormatterBundle>(
    () => ({
      number: (l, v, opts) =>
        formatNumber(l as Parameters<typeof formatNumber>[0], v, opts),
      currency: (l, v, c) =>
        formatCurrency(l as Parameters<typeof formatCurrency>[0], v, c),
      date: (l, v, opts) =>
        formatDate(l as Parameters<typeof formatDate>[0], v, opts),
    }),
    [],
  );

  const columns = React.useMemo<ColumnDef<InvoiceRow>[]>(
    () =>
      defineColumns<InvoiceRow>([
        {
          key: "number",
          header: "Number",
          type: "text",
          width: 160,
          sortable: true,
          filterable: true,
          // Mono font for invoice numbers — they're identifiers, not prose.
          cell: ({ row }) => (
            <span className="font-mono text-xs">{row.number}</span>
          ),
        },
        {
          key: "partnerId",
          header: "Partner",
          type: "relation",
          width: 220,
          sortable: true,
          filterable: true,
          // The relation resolver returns the display label for chips and
          // exports; the table also uses it for the cell content.
          relationLabel: (value) => PARTNERS[String(value)] ?? "Unknown",
        },
        {
          key: "total",
          header: "Total",
          type: "money",
          currency: "EUR",
          width: 140,
          align: "right",
          sortable: true,
          filterable: true,
        },
        {
          key: "vatRate",
          header: "VAT",
          type: "percent",
          width: 90,
          align: "right",
          sortable: true,
          filterable: true,
        },
        {
          key: "issuedAt",
          header: "Issued",
          type: "datetime",
          width: 160,
          sortable: true,
          filterable: true,
        },
        {
          key: "dueDate",
          header: "Due",
          type: "date",
          width: 130,
          sortable: true,
          filterable: true,
        },
        {
          key: "status",
          header: "Status",
          type: "enum",
          width: 120,
          sortable: true,
          filterable: true,
          enumLabels: STATUS_LABELS,
        },
        {
          key: "isPaid",
          header: "Paid",
          type: "boolean",
          width: 80,
          align: "center",
          sortable: true,
          filterable: true,
        },
      ]),
    [],
  );

  /**
   * Real export wiring. The DataTable engine routes the menu's format id
   * here, plus a snapshot of the post-sort + post-filter rows and the
   * currently-visible columns — so the file the user gets matches what they
   * see on screen.
   */
  const handleExport = React.useCallback(
    async (
      format: ExportFormat,
      snapshot: { rows: InvoiceRow[]; columns: ColumnDef<InvoiceRow>[] },
    ) => {
      try {
        const filename = `invoices-${new Date().toISOString().slice(0, 10)}`;
        if (format === "csv") {
          const blob = toCsv({
            rows: snapshot.rows,
            columns: snapshot.columns,
            locale,
            formatters,
          });
          downloadBlob(blob, `${filename}.csv`);
          toast.success(`Exported ${snapshot.rows.length} rows as CSV`);
        } else if (format === "xlsx") {
          const blob = await toXlsx({
            rows: snapshot.rows,
            columns: snapshot.columns,
            locale,
            formatters,
            sheetName: "Invoices",
          });
          downloadBlob(blob, `${filename}.xlsx`);
          toast.success(`Exported ${snapshot.rows.length} rows as XLSX`);
        } else if (format === "pdf") {
          // Cap PDF at the first 200 rows — @react-pdf/renderer is heavy and
          // a 1000-row print preview takes ~8s in the browser. The cap keeps
          // the demo responsive; production callers route through a server
          // job for full-dataset PDFs.
          const limited = snapshot.rows.slice(0, 200);
          const blob = await toPdf({
            rows: limited,
            columns: snapshot.columns,
            locale,
            formatters,
            title: "Invoices",
          });
          downloadBlob(blob, `${filename}.pdf`);
          toast.success(`Exported ${limited.length} rows as PDF`);
        }
      } catch (error) {
        // Surface the failure rather than swallow it (per "no silent
        // fallbacks"). The toast is the user-facing channel; the console
        // keeps the stack for diagnostics.
        const message =
          error instanceof Error ? error.message : "Unknown export error";
        console.error("[showcase/table] export failed:", error);
        toast.error(`Export failed: ${message}`);
      }
    },
    [locale, formatters],
  );

  /**
   * Bulk action: "Mark as paid". The engine routes the selected row ids to
   * the action's `onRun` callback; here we just toast the user since
   * there's no persistence layer yet.
   */
  const handleMarkAsPaid = React.useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    toast.success(
      ids.length === 1
        ? "Marked 1 invoice as paid"
        : `Marked ${ids.length} invoices as paid`,
    );
  }, []);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Table</h1>
        <p className="mt-1 text-fg-muted">
          1000-row virtualized dataset with mixed column types, filter operators,
          export to CSV / XLSX / PDF, saved views (localStorage), and bulk actions.
        </p>
      </header>

      <DataTable<InvoiceRow>
        columns={columns}
        data={ROWS}
        rowId={(row) => row.id}
        persistKey="showcase:table:invoices"
        locale={locale}
        formatters={formatters}
        onExport={handleExport}
        primaryAction={
          <Button>
            <Plus className="size-4" /> New invoice
          </Button>
        }
        bulkActions={[
          {
            id: "mark-paid",
            label: "Mark as paid",
            onRun: handleMarkAsPaid,
          },
        ]}
        onRowActivate={(row) => {
          toast.message(`Opened ${row.number}`, {
            description: `Partner: ${row.partnerName}`,
          });
        }}
      />
    </div>
  );
}
